import React, { useState, useEffect, useCallback, useMemo, useRef, startTransition } from "react";
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Slide from '@mui/material/Slide';
import TextField from '@mui/material/TextField';
import MenuOpenRoundedIcon from '@mui/icons-material/MenuOpenRounded';
import Alert from '@mui/material/Alert';
import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

import TranslationFields from "./TranslationFields";
import LoadingIndicator from "./LoadingIndicator";

const flattenObject = function(ob) {
	var toReturn = {};
	for (var i in ob) {
		if (!ob.hasOwnProperty(i)) continue;
		else if (typeof ob[i] == 'object' && ob[i] !== null) {
			var flatObject = flattenObject(ob[i]);
			for (var x in flatObject) {
				if (!flatObject.hasOwnProperty(x)) continue;
				toReturn[i + '.' + x] = flatObject[x];
			}
		}
		else {
			toReturn[i] = ob[i];
		}
	}
	return toReturn;
};
// true, false and null are used programmatically: their rows are hidden unless switched on
const isProgrammaticValue = (value) => value === null || typeof value === 'boolean';
function compareObjects(a, b) {
	let fa = flattenObject(a);
	let fb = flattenObject(b);
	if(fa.length != fb.length) return false;
	for (var i in fa) {
		let key = i;
		// targets saved before null values were kept lack those keys; set() fills them in
		if(!fb.hasOwnProperty(key) && fa[key] !== null) return false;
	}
  return true;
}
function parseString(str) {

	// Try to parse as boolean first
	if (typeof str === 'string') {
		str = str.trim();
		if (str.toLowerCase() === "true") return true;
		if (str.toLowerCase() === "false") return false;
	}
	if (typeof str === 'boolean') {
		return str;
	}

	// Try to parse as a number
	if (!isNaN(str) && str !== "") {
    let num = Number(str);
    return num;
  }

	// Return the original string if it can't be parsed as boolean or number
	return str;
}
// sourceValue: the original's value at this path. A string there stays a string ("44" in a text
// list must not turn into 44); only non-string originals are parsed back to numbers and booleans
function set(obj, path, value, sourceValue) {
	var schema = obj;  // a moving reference to internal objects within obj
	var pList = path.split('.');
	var len = pList.length;
	for(var i = 0; i < len-1; i++) {
			var elem = pList[i];

			// Prüfen, ob das nächste Element im Pfad eine Zahl ist
			var nextElemIsArrayIndex = !isNaN(parseInt(pList[i + 1]));

			// Wenn das aktuelle Element nicht existiert, anlegen
			if (!schema[elem]) {
					// Falls das nächste Element ein Index ist, erstelle ein Array, ansonsten ein Objekt
					schema[elem] = nextElemIsArrayIndex ? [] : {};
			}

			// Falls das existierende Element ein Array ist und das nächste Element ein Objekt sein soll
			if (Array.isArray(schema[elem]) && !nextElemIsArrayIndex) {
					schema[elem] = {};
			}

			// Falls das existierende Element ein Objekt ist und das nächste Element ein Index sein soll
			if (!Array.isArray(schema[elem]) && nextElemIsArrayIndex) {
					schema[elem] = [];
			}
			schema = schema[elem];
	}
	if (typeof sourceValue === 'string') schema[pList[len-1]] = String(value ?? '');
	// parseString would turn null into 0
	else if (value === null) schema[pList[len-1]] = null;
	else schema[pList[len-1]] = parseString(value);
}



const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const JsonEditor = (props) => {
	const { t } = useTranslation();
	let sourceObject = {};
	let targetObject = {};
	let _flatSourceObject = null;
	let _flatTargetObject = null;

	const rightMargin = (props.parentField === 'customfields' || window.location.pathname.indexOf('/custom') >= 0);

  const [open, setOpen] = useState(false);
  //const [sourceObject, setSourceObject] = useState({});
  const [finalTargetObject, setFinalTargetObject] = useState({});
  const [flatSourceObject, setFlatSourceObject] = useState(null);
  const [flatTargetObject, setFlatTargetObject] = useState(null);
	// read by the memoized handleChange, which would see a stale state value
	const flatSourceRef = useRef({});
  const [error, setError] = useState(null);
  const [fields, setFields] = useState(null);
  // the field list of a big metafield takes a moment to render; the dialog slides in with the
  // spinner first and mounts the list as a transition afterwards, so the spinner keeps moving
  const [ready, setReady] = useState(false);
	const [showProgrammatic, setShowProgrammatic] = useState(false);
	// hidden rows stay in finalTargetObject with their current value
	const visibleFields = useMemo(() => (fields && !showProgrammatic) ? fields.filter(f => !f.programmatic) : fields, [fields, showProgrammatic]);
	const hasProgrammatic = !!fields?.some(f => f.programmatic);


	// Initialisation
  useEffect(() => {
		try {
			sourceObject = JSON.parse(props.source);
			// "36" or "true" parse as JSON too, but only objects and arrays have keys to edit
			if(sourceObject === null || typeof sourceObject !== 'object') {
				throw new Error('not a JSON structure');
			}
			_flatSourceObject = flattenObject(sourceObject);
			try {
				targetObject = JSON.parse(props.target);
				_flatTargetObject = flattenObject(targetObject);

				if(!compareObjects(sourceObject, targetObject)) {
					setError('structure')
				} else {
					setError(null);
				}
			}
			catch (e) {}
		}
		catch (e) {}

		if(_flatSourceObject) {
			let newFields = [];
			Object.keys(_flatSourceObject).forEach((key) => {
				newFields.push({
					id: key,
					type: 'text',
					label: key,
					source: String(_flatSourceObject[key]),
					programmatic: isProgrammaticValue(_flatSourceObject[key]),
					target: (_flatTargetObject && _flatTargetObject[key]) ? _flatTargetObject[key] : ''
				});
			});
			setFields(newFields);
			setFlatSourceObject(_flatSourceObject);
			setFlatTargetObject(_flatTargetObject);
		}
		// the field menu shows its "Translate JSON object" entry only for JSON structures
		if(props.onAvailable) props.onAvailable(!!_flatSourceObject);

  }, [props.source, props.target]);



  const handleOpen = () => {
		sourceObject = JSON.parse(props.source);
		_flatSourceObject = flattenObject(sourceObject);
		flatSourceRef.current = _flatSourceObject;
		try {
			targetObject = JSON.parse(props.target);
			_flatTargetObject = flattenObject(targetObject);

			if(!compareObjects(sourceObject, targetObject)) {
				setError('structure');
			} else {
				setError(null);
			}
		}
		catch (e) {}
		// the target's top level takes the source's shape: a list metafield must stay an array
		if(targetObject === null || typeof targetObject !== 'object' || Array.isArray(targetObject) !== Array.isArray(sourceObject)) {
			targetObject = Array.isArray(sourceObject) ? [] : {};
		}


		let newFields = [];
		Object.keys(_flatSourceObject).forEach((key) => {
			// a translated 0 or false is a value, not an empty field
			const targetValue = _flatTargetObject?.[key];
			const hasTarget = targetValue !== undefined && targetValue !== null && targetValue !== '';
			newFields.push({
				id: key,
				type: 'text',
				label: key,
				// the source row renders only truthy values: false and 0 showed as an empty field
				source: String(_flatSourceObject[key]),
				programmatic: isProgrammaticValue(_flatSourceObject[key]),
				target: hasTarget ? String(targetValue) : ''
			});

			if (hasTarget) {
				set(targetObject, key, targetValue, _flatSourceObject[key]);
			} else {
				set(targetObject, key, _flatSourceObject[key], _flatSourceObject[key]);
			}
		});
		setFinalTargetObject(targetObject);

		setFields(newFields);
		setFlatSourceObject(_flatSourceObject);
		setFlatTargetObject(_flatTargetObject);
		setReady(false);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
		setReady(false);
  };

	// opened from the field menu: the parent bumps the counter instead of rendering our button
	useEffect(() => {
		if(props.openRequest && flatSourceObject) handleOpen();
	}, [props.openRequest]);

  const handleEditingDone = () => {
		props.onChange(null, {
			id: props.id,
			changed: true,
			initial: props.target,
			target: JSON.stringify(finalTargetObject),
			parentField: props.parentField,
		})

    setOpen(false);
		setReady(false);
  };

	// stable identity: a new handler per render re-rendered every row on each keystroke
	const handleChange = useCallback((e, data) => {
		setFinalTargetObject(current => {
			let newTargetObject = Array.isArray(current) ? [...current] : {...current};
			set(newTargetObject, data.id, data.target, flatSourceRef.current[data.id]);
			return newTargetObject;
		});
		setFields(current => {
			let newFields = [...current];
			let editField = newFields.find(x => x.id === data.id);
			if(editField) editField.target = data.target;
			return newFields;
		});
	}, []);

	const importFromOriginal = () => {
		props.onChange(null, {
			id: props.id,
			changed: true,
			initial: props.target,
			target: props.source,
			parentField: props.parentField,
		})
	};

	return (
        <div>
            {flatSourceObject && !props.hideButton &&
				<Tooltip title="View as formatted JSON" arrow>
					<IconButton color="inherit" component="span" size="small" variant="contained" sx={styles.openBtn} style={{marginRight: rightMargin ? 40 : 0}} onClick={handleOpen}>
						<MenuOpenRoundedIcon />
					</IconButton>
				</Tooltip>
			}
            <Dialog
              open={open}
              TransitionComponent={Transition}
              TransitionProps={{ onEntered: () => startTransition(() => setReady(true)) }}
              onClose={handleClose}
                      fullWidth={true}
                      maxWidth="md"
              aria-labelledby="alert-dialog-slide-title"
              aria-describedby="alert-dialog-slide-description"
            >
              <DialogTitle id="alert-dialog-slide-title">Formatted JSON</DialogTitle>
                      <Box sx={styles.msgBox}>
                          {error === 'structure' ?
                              <Alert severity="error">
                                  The object structure of your target does not match with the original structure.
                              </Alert>
                          :
                              <Alert severity="info">Note: Do not translate fields that could be used programmatically, such as <strong>"null"</strong>, <strong>"true"</strong>, and <strong>"false"</strong>. Fields with these values are hidden by default.</Alert>
                          }
                      </Box>
              <DialogContent sx={{ '& .static-list': { display: 'flex', flexDirection: 'column', gap: '24px' }, '& .field-row': { '--Grid-rowSpacing': '0px' } }}>
                      {flatSourceObject ?
                          <div>
                              {(fields && error === null )?
                                  (ready ?
                                      <TranslationFields
                                          data={visibleFields}
                                          nested
                                          from={props.from}
                                          to={props.to}
                                          onChange={handleChange}
                                          parentField="json-editor"
                                      />
                                  :
                                      // the transform keeps the indicator's fixed overlay inside this box; the global
                                      // #loadingIndicator rule offsets it for the sidebar, which would push it off centre here
                                      <Box sx={{ position: 'relative', minHeight: 240, transform: 'translate(0, 0)', '&& #loadingIndicator': { left: '0 !important' } }}>
                                          <LoadingIndicator pending withLogo={false} message={t('itemsList.jsonEditorLoading')} />
                                      </Box>
                                  )
                              :
                                  <p style={{textAlign: 'center', padding: 16}}>
                                      It is necessary that both the original and the target object have an identical structure.<br/>
                                      This can be fixed by importing the original content.<br/>
                                      <Button variant="outlined" color="primary" onClick={importFromOriginal} style={{marginTop: 8}}>Import from original</Button>
                                  </p>
                              }
                          </div>
                          :
                          <p>No items found in object.</p>
                      }
              </DialogContent>

                      {error === null &&
                          <DialogActions>
                              {hasProgrammatic &&
                                  <FormControlLabel
                                      sx={{ mr: 'auto', ml: 1 }}
                                      control={<Switch checked={showProgrammatic} onChange={e => setShowProgrammatic(e.target.checked)} />}
                                      label='Show "true", "false" and "null" fields'
                                  />
                              }
                              <Button variant="outlined" onClick={handleClose}>
                                  Cancel
                              </Button>
                              <Button variant="contained" onClick={handleEditingDone} color="primary" endIcon={<CheckIcon />}>
                                  Editing Done
                              </Button>
                          </DialogActions>
                      }
            </Dialog>
        </div>
    );
}


const styles = {
  msgBox: {
    width: '100%',
    '& > *': {
      borderRadius: 0,
    },
  },
	openBtn: {
		position: 'absolute',
		top: 0,
		right: 0,
	}
};

export default JsonEditor;
